import React, { useState, useMemo, useEffect } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { debounce } from '@mui/material/utils'
import Close from '@mui/icons-material/Close'
import {
  connectionsPanelSx,
  connectionsPanelWithScrollAreaSx,
  connectionsScrollAreaSx,
} from './ConnectionsLayout'
import type { WidgetConnectionsV3Props, SupportedConnector } from './types'

export function WidgetConnectionsV3({
  widgetModule,
  data,
  connectorComponentStyle,
}: WidgetConnectionsV3Props) {
  const { useHotglue, Connections } = widgetModule
  const { initialized, getSupportedConnectors } = useHotglue({
    tenantId: data.tenant,
    environmentId: data.envId,
    apiKey: data.apiKey,
    jwtToken: data.jwt,
    operation: data.options?.operation,
  })

  const [searchInput, setSearchInput] = useState('')
  const [filterSearch, setFilterSearch] = useState('')
  const [connectors, setConnectors] = useState<Array<SupportedConnector>>([])
  const [connectorsLoaded, setConnectorsLoaded] = useState(false)

  useEffect(() => {
    if (!initialized || data.type !== 'all') {
      setConnectorsLoaded(data.type !== 'all')
      return
    }
    setConnectorsLoaded(false)
    let isMounted = true

    const loadConnectors = async () => {
      try {
        const supported = (await getSupportedConnectors()) as Array<{
          id: string
          label?: string
          name?: string
        }>
        if (!isMounted) return
        setConnectors(supported ?? [])
        setConnectorsLoaded(true)
      } catch {
        if (!isMounted) return
        setConnectors([])
        setConnectorsLoaded(true)
      }
    }

    loadConnectors()
    return () => {
      isMounted = false
    }
  }, [data.type, initialized])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFilterSearch(e?.target?.value ?? '')
  }

  const debouncedHandleChange = useMemo(
    () => debounce(handleChange, 300),
    []
  )

  useEffect(() => {
    return () => debouncedHandleChange.clear()
  }, [debouncedHandleChange])

  const handleSearchInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchInput(e.target.value)
    debouncedHandleChange(e)
  }

  const handleClearSearch = () => {
    debouncedHandleChange.clear()
    setSearchInput('')
    setFilterSearch('')
  }

  const normalizedSearch = filterSearch.trim().toLowerCase()
  const filteredConnectorIds = useMemo(() => {
    if (!normalizedSearch) return undefined
    // Don't apply search filter until connectors have loaded; otherwise we'd filter over []
    // and show zero results while still loading
    if (!connectorsLoaded) return undefined
    const matched = connectors
      .filter(
        (connector) =>
          connector.id.toLowerCase().includes(normalizedSearch) ||
          connector.label?.toLowerCase().includes(normalizedSearch) ||
          connector.name?.toLowerCase().includes(normalizedSearch)
      )
      .map((connector) => connector.id)
    return matched.length > 0 ? matched : ['__no_matching_connector__']
  }, [connectors, normalizedSearch, connectorsLoaded])

  const connectorFilter = useMemo(() => {
    const fromToken = data.entityId ? [data.entityId] : undefined
    if (fromToken && filteredConnectorIds) {
      const tokenConnectorInSearch = filteredConnectorIds.includes(data.entityId)
      return tokenConnectorInSearch ? fromToken : ['__no_matching_connector__']
    }
    return filteredConnectorIds ?? fromToken
  }, [data.entityId, filteredConnectorIds])

  const filter = useMemo(() => {
    const flowFilter = data.flow ? [data.flow] : undefined
    const hasFlow = Boolean(flowFilter?.length)
    const hasConnectors = Boolean(connectorFilter?.length)
    if (!hasFlow && !hasConnectors) return undefined
    return {
      ...(hasFlow && { flows: flowFilter }),
      ...(hasConnectors && { connectors: connectorFilter }),
    }
  }, [connectorFilter, data.flow])

  const hasNoSearchResults =
    connectorFilter?.length === 1 &&
    connectorFilter[0] === '__no_matching_connector__'

  const useConnectionsScrollLayout = data?.type === 'all'
  const hideBackButtons = data.options?.hideBackButtons === true
  const hasSearchbar =
    useConnectionsScrollLayout && !connectorComponentStyle
  const panelSx = {
    ...(useConnectionsScrollLayout
      ? connectionsPanelWithScrollAreaSx
      : connectionsPanelSx),
    ...(connectorComponentStyle && {
      p: 0,
      border: 'none',
      boxShadow: 'none',
      '& .hotglue-connections-root > div > div:last-child': { // TODO: This is a hack to remove the border bottom from the last child.
        borderBottom: 'none !important',
      },
    }),
  }

  return (
    <Paper sx={panelSx}>
      {hasSearchbar && (
        <Box
          display="flex"
          flexWrap="wrap"
          gap={2}
          alignItems="flex-start"
          mb={3}
          flexShrink={0}
        >
          <TextField
            size="small"
            placeholder="Search app"
            type="text"
            value={searchInput}
            onChange={handleSearchInput}
            sx={{ flex: '1 1 200px', minWidth: 0, width: '100%' }}
            InputProps={{
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    aria-label="Clear search"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>
      )}
      {useConnectionsScrollLayout ? (
        <Box sx={connectionsScrollAreaSx}>
          {hasNoSearchResults ? (
            <Box
              display="flex"
              flex={1}
              minHeight={0}
              alignItems="center"
              justifyContent="center"
              px={2}
            >
              <Typography color="text.tertiary" textAlign="center">
                No apps found. Try a different search.
              </Typography>
            </Box>
          ) : (
            <Connections
              flows={false}
              filter={filter}
              listStyle={{ gap: 24, paddingRight: 16 }}
              connectorComponentStyle={connectorComponentStyle}
              hideBackButtons={hideBackButtons}
            />
          )}
        </Box>
      ) : (
        <Connections
          flows={false}
          filter={filter}
          listStyle={{ gap: 24, paddingRight: 16 }}
          connectorComponentStyle={connectorComponentStyle}
          hideBackButtons={hideBackButtons}
        />
      )}
    </Paper>
  )
}
